import { useAppState } from "./useAppState";
import { useMapnetCoverageEnabled } from "../flag-hooks/useMapnetCoverage";
import { useMctFlags } from "../flag-hooks/useMctFlags";
import type { Plan } from "../../@types";
import { isMedicareAdvantageType } from "../planTypes";
import { usePlanType } from "../planHelpers";
import { useProviderNetwork } from "./useProviderNetwork";

/**
 * This hook is a dependency of the original flag for all of our provider network
 * features, so additionally looks at that flag.
 *
 * Optionally, takes in a `plan.contract year`, which will take precedence over search year
 *
 * The hook without passing in `plan.contract_year` can be used to conditionally
 * render the route at all, for example. The UI that links facility routes is
 * gated by a plan's type and contract year.
 *
 * @example `const { enableFacilitiesManagement } = useEnableFacilitiesManagement();`
 * @example `const { enableFacilitiesManagement } = useEnableFacilitiesManagement(plan.contract_year);`
 */
export const useEnableFacilitiesManagement = (
  planContractYear?: Plan["contract_year"]
): { enableFacilitiesManagement: boolean } => {
  /**
   * `year` is typed as `string | undefined`, but facilities management is only
   * reachable from plan details, which requires the choosing a search year,
   * viewing that year's results, then opening a plan. By then the year is set.
   * If it somehow isn't, `Number(undefined)` is `NaN`. This returns `false`,
   * and which correctly returns a user to the landing page (fall through route).
   */
  const { lastViewedPlan, year: planSearchYear } = useAppState();
  const planType = usePlanType();
  const year = Number(planContractYear ?? planSearchYear);
  const mapnetCoverageEnabled = useMapnetCoverageEnabled(year);
  const { enableProviderNetwork } = useProviderNetwork();
  const { tmpFeEnableMapnetFacilityNetwork } = useMctFlags();
  const enableFacilitiesManagement =
    mapnetCoverageEnabled &&
    tmpFeEnableMapnetFacilityNetwork &&
    enableProviderNetwork &&
    !!lastViewedPlan &&
    !!planType &&
    isMedicareAdvantageType(planType);
  return { enableFacilitiesManagement };
};
